import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import type { BusinessUnitManagementDetailModel } from '../../../../models';

/**
 * BusinessUnitGeneralTabComponent
 *
 * Displays general information about a business unit including statistics
 */
@Component({
  selector: 'dtis-admin-business-unit-general-tab',
  imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatChipsModule, MatTooltipModule, TranslocoModule],
  styles: [
    `
    mat-chip {
      pointer-events: none;
    }
  `,
  ],
  template: `
    @if (businessUnit(); as bu) {
      <div class="p-6">
        <h2 class="text-title-large mb-6 font-medium">
          {{ 'general-information' | transloco }}
        </h2>

        <!-- General Information Card -->
        <mat-card appearance="outlined">
          <mat-card-content class="p-6!">
            <div class="divide-y divide-outlineVariant flex flex-col gap-2">
              <!-- BU Name with Edit Button -->
              <div class="flex items-start justify-between py-2">
                <div class="flex flex-col gap-1">
                  <span class="text-body-medium text-onSurfaceVariant">
                    {{ 'business-unit-name' | transloco }}
                  </span>
                  <span class="text-body-large font-medium">
                    {{ bu.name }}
                  </span>
                </div>
              </div>

              <!-- Statistics -->
              <div class="flex flex-col gap-2 py-2">
                <span class="text-body-medium text-onSurfaceVariant">
                  {{ 'statistics' | transloco }}
                </span>
                <div class="flex flex-wrap gap-2">
                  <!-- Administrators Chip -->
                  <mat-chip>
                    {{ adminCount() }} {{ 'administrators' | transloco }}
                  </mat-chip>
                  
                  <!-- Users Chip -->
                  <mat-chip>
                    {{ userCount() }} {{ 'users' | transloco }}
                  </mat-chip>

                  <!-- Master Count Chip -->
                  <mat-chip>
                    {{ masterCount() }} {{ 'masters' | transloco }}
                  </mat-chip>

                  <!-- Instance Count Chip -->
                  <mat-chip>
                    {{ instanceCount() }} {{ 'instances' | transloco }}
                  </mat-chip>
                </div>
              </div>
              <!-- Create Date -->
              <div class="flex flex-col gap-1 py-2">
                <span class="text-body-medium text-onSurfaceVariant">
                  {{ 'created-date' | transloco }}
                </span>
                <span class="text-body-large">
                  {{ 'not-available' | transloco }}
                </span>
              </div>
              <!-- BU ID -->
              <div class="flex flex-col gap-1 py-2">
                <span class="text-body-medium text-onSurfaceVariant">
                  {{ 'business-unit-id' | transloco }}
                </span>
                <div class="flex items-center gap-1">
                  <span class="text-body-large">{{ bu.identifier }}</span>
                  <button
                    mat-icon-button type="button"
                    class="h-9! w-9!"
                    (click)="copyToClipboard(bu.identifier)"
                    [matTooltip]="'copy-id' | transloco"
                  >
                    <mat-icon class="text-lg!">content_copy</mat-icon>
                  </button>
                </div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    }
  `,
})
export class BusinessUnitGeneralTabComponent {
  private readonly clipboard = inject(Clipboard);
  private readonly snackBar = inject(MatSnackBar);

  businessUnit = input.required<BusinessUnitManagementDetailModel>();
  masterCount = input.required<number>();
  instanceCount = input.required<number>();

  editBusinessUnit = output<void>();

  adminCount = computed(() => {
    const users = this.businessUnit().users ?? [];
    return users.filter((u) => u.roles?.includes('Administrator')).length;
  });

  userCount = computed(() => {
    const users = this.businessUnit().users ?? [];
    return users.filter((u) => u.roles?.includes('User') || !u.roles?.includes('Administrator')).length;
  });

  copyToClipboard(text: string): void {
    this.clipboard.copy(text);
    this.snackBar.open('ID copied to clipboard', undefined, {
      duration: 2000,
    });
  }
}
